import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { NucleusUserNotifierContract } from '@nucleus/user-ui';

/**
 * Tasksense notifier — SweetAlert2 chrome matching BaseComponent's
 * showSuccess/showError/showWarning helpers so the library's toasts
 * visually match the rest of the app. Mirrors bookwise + goldfish notifier
 * adapters.
 */
@Injectable({ providedIn: 'root' })
export class TasksenseUserNotifierAdapter implements NucleusUserNotifierContract {
  private readonly router = inject(Router);

  success(message: string): void {
    Swal.fire({
      title: 'Success',
      html: message,
      icon: 'success',
      showCancelButton: false,
      timer: 6000,
      confirmButtonColor: '#4169E1',
      confirmButtonText: 'Ok',
    });
  }

  warning(message: string): void {
    Swal.fire({
      title: 'Warning',
      html: message,
      icon: 'warning',
      showCancelButton: false,
      timer: 6000,
      confirmButtonColor: '#4169E1',
      confirmButtonText: 'Ok',
    });
  }

  error(message: string): void {
    Swal.fire({
      title: 'Error',
      html: message,
      icon: 'error',
      showCancelButton: false,
      timer: 6000,
      confirmButtonColor: '#4169E1',
      confirmButtonText: 'Ok',
    });
  }

  confirm(title: string, message: string): Promise<boolean> {
    return new Promise((resolve) =>
      Swal.fire({
        title,
        html: message,
        icon: 'question',
        showCancelButton: true,
        timer: 6000,
        confirmButtonColor: '#4169E1',
        confirmButtonText: 'Yes',
        cancelButtonColor: '#4169E1',
        cancelButtonText: 'No',
      }).then((result) => resolve(result.isConfirmed)),
    );
  }

  accessDenied(): void {
    Swal.fire({
      title: 'Access Deny',
      html: "You don't have the required permissions to view this page.",
      icon: 'error',
      showCancelButton: false,
      timer: 6000,
      confirmButtonColor: '#4169E1',
      confirmButtonText: 'Ok',
    });
    this.router.navigate(['/home']);
  }

  downloadReport(blob: Blob, filename: string, mimeType: string): void {
    /* Tasksense doesn't have a FileViewerDialog (yet), so fall back to a
     * plain anchor-tag download. Future: route through a shared preview
     * dialog like bookwise. */
    const url = URL.createObjectURL(new Blob([blob], { type: mimeType }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
